import React from "react";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import { Stack } from "@UI/Components/Stack/Stack";
import { Text } from "@UI/Components/Text/Text";
import { Icon } from "@UI/Components/Icon/Icon";
import { Calendar } from "@UI/Components/Icons/Calendar";
import { Flex } from "@UI/Components/Flex/Flex";
import messages from "./messages";
import { ITrackingCard, SingleTrackingCard } from "./SingleTrackingCard";
import useMediaQuery from "@UI/hooks/useMediaQuery";
import {
  IServiceProviderRealisationRateMetrics,
  IServiceProviderRepeatRateMetrics,
} from "@modules/ServiceProviders/services/serviceProviders";

interface ITrackingsInfosProps {
  metrics:
    | IServiceProviderRepeatRateMetrics
    | IServiceProviderRealisationRateMetrics;
}

export const TrackingsInfos = ({ metrics }: ITrackingsInfosProps) => {
  const { isMobile } = useMediaQuery();

  const getRangeMessage = {
    blocked: <FormattedMessage {...messages.blocked} />,
    warning: <FormattedMessage {...messages.warning} />,
    ok: <FormattedMessage {...messages.ok} />,
    good: <FormattedMessage {...messages.good} />,
    excellent: <FormattedMessage {...messages.excellent} />,
    newbie: <FormattedMessage {...messages.newbie} />,
  };

  const typedMetrics = metrics as IServiceProviderRealisationRateMetrics &
    IServiceProviderRepeatRateMetrics;

  const isRealisationRate = typedMetrics?.details?.closedServices !== undefined;

  const checkEmptyValue = () => {
    if (
      (typedMetrics?.details?.cancelledServices === 0 &&
        typedMetrics?.details?.closedServices === 0) ||
      (typedMetrics?.details?.recurrentClients === 0 &&
        typedMetrics?.details?.totalClients === 0)
    ) {
      return true;
    }
    return false;
  };

  const scoreValue = metrics?.metric?.value;
  const scoreNode = typeof scoreValue === "number" ? scoreValue : null;

  const totalServices =
    (typedMetrics?.details?.closedServices ?? 0) +
    (typedMetrics?.details?.cancelledServices ?? 0);

  const detailCards = metrics
    ? Object.entries(metrics.details).map((element, index) => (
        <SingleTrackingCard
          key={index}
          type={element[0] as ITrackingCard["type"]}
          value={element[1] ?? <FormattedMessage {...messages.no_information} />}
        />
      ))
    : [];

  if (isRealisationRate && !checkEmptyValue()) {
    detailCards.push(
      <SingleTrackingCard
        key="totalServices"
        type="totalServices"
        value={totalServices}
      />
    );
  }

  return (
    <Flex flexDirection="column">
      <Flex justifyContent="flex-end">
        <Text as="span" fontSize={{ base: "sm", md: "md" }}>
          <FormattedMessage
            {...messages.dateComputed}
            values={{
              calendar: () => (
                <Icon as={Calendar} marginRight={1} display="inline" />
              ),
            }}
          />
        </Text>
      </Flex>
      <Stack
        direction={["column", "row"]}
        mt={{ base: 2, md: 3 }}
        spacing={{ base: 2, md: 3 }}
      >
        <SingleTrackingCard
          type="score"
          value={
            checkEmptyValue() ? (
              <FormattedMessage {...messages.no_information} />
            ) : (
              <FormattedMessage
                {...messages.score_value}
                values={{ score: scoreNode }}
              />
            )
          }
          valueExplanation={
            isRealisationRate && !checkEmptyValue() ? (
              <FormattedMessage
                {...messages.scoreFormula}
                values={{
                  closed: typedMetrics.details.closedServices,
                  total: totalServices,
                }}
              />
            ) : undefined
          }
          valueSubInfos={getRangeMessage[metrics?.metric?.currentLevel]}
        />
        {!isMobile && detailCards}
      </Stack>
      {isMobile && (
        <Stack
          direction="row"
          mt={{ base: 2, md: 3 }}
          spacing={{ base: 2, md: 3 }}
        >
          {detailCards}
        </Stack>
      )}
    </Flex>
  );
};
